/**
 * PdfPopupViewer - a PDF in an in-app popup with a "Download" button
 * option (Bill 2026-09-11, online applications: "opens the PDF in an in-app
 * popup viewer with an 'open in new window' option on the pop-up pane").
 *
 * The bytes come from an authenticated API call and are drawn by pdf.js
 * (PdfCanvasViewer). CHG-0097: they used to go to an <iframe>, which is an
 * empty grey box on bank PCs (built-in PDF viewer off by policy) — the blank
 * Signature Card. "Download" saves the blob: URL as a file (Teams desktop cannot open it in a new window), revoked on close.
 */
import { useEffect, useRef, useState } from 'react';
import { Download } from '@retail-ops/panel-shell/fi';
import { Button, ModalShell } from '@/components/primitives';
import { PdfCanvasViewer } from './PdfCanvasViewer';
import './PdfPopupViewer.css';

export interface PdfPopupViewerProps {
  open: boolean;
  title: string;
  /** Fetches the PDF bytes. Called once each time the popup opens. */
  load: () => Promise<Blob>;
  onClose: () => void;
}

/**
 * Saves the PDF as a file. Teams desktop cannot open a blob: URL in a new window (666 test 2026-11-04,
 * Documents > Signed packets), but it does save an anchor download, the same way the Aurora package and the
 * customer copy are saved.
 */
export function savePdf(url: string, title: string): void {
  const name = `${title.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() || 'document'}.pdf`;
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function PdfPopupViewer({ open, title, load, onClose }: PdfPopupViewerProps): JSX.Element | null {
  const [url, setUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The loader is read through a ref so a parent re-render never restarts
  // the fetch; only opening does.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setBlob(null);
    setError(null);
    loadRef.current()
      .then((loaded) => {
        if (!live) return;
        const pdf = loaded.type === 'application/pdf' ? loaded : new Blob([loaded], { type: 'application/pdf' });
        objectUrl = URL.createObjectURL(pdf);
        setUrl(objectUrl);
        setBlob(pdf);
      })
      .catch((e: unknown) => {
        if (!live) return;
        const msg = e instanceof Error ? e.message : '';
        setError(/404|not found/i.test(msg)
          ? 'No application PDF is linked to this submission.'
          : 'Could not load the application PDF. Try again, or report it from the Debug icon.');
      });
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open]);

  if (!open) return null;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={title}
      size="xl"
      footer={
        <div className="pdf-popup__footer">
          <Button
            variant="ghost"
            size="sm"
            startIcon={<Download size={14} />}
            disabled={!url}
            onClick={() => { if (url) savePdf(url, title); }}
          >
            Download
          </Button>
          <Button variant="primary" size="sm" onClick={onClose}>Close</Button>
        </div>
      }
    >
      <div className="pdf-popup__body">
        {error ? (
          <div className="pdf-popup__error" role="alert">{error}</div>
        ) : blob ? (
          <PdfCanvasViewer className="pdf-popup__frame" source={blob} title={title} />
        ) : (
          <div className="pdf-popup__loading" role="status">Loading PDF…</div>
        )}
      </div>
    </ModalShell>
  );
}
